// The few shared pieces every page uses. Plain elements with classes from
// theme.css; no component library (conventions/stack.md: default to no).
import { Children, createContext, Fragment, isValidElement, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { headline, type Problem } from "./api/problem";
import { useTermIfScoped } from "./lexicon";
import { ErrorCount, fieldError, formErrors, optional, sameValues } from "./field";
import { useUnsavedGuard } from "./unsavedGuard";
import { copyValue } from "./copy";
import { BlinkIcon, ConsoleIcon, CopyIcon, DeleteIcon, DoneIcon, EditIcon, FactoryIcon, FirmwareIcon, Glyph, InfoIcon, MoreIcon, PlanIcon, PortsIcon, PowerIcon, RebootIcon, RefreshIcon, type LucideIcon } from "./icons";

// The card, drawer, sheet and inspector every page shares (#1890), with their
// Escape and the return of focus, drawn once in `surfaces.tsx`.
export { Card, Dialog, Drawer, Inspector, Sheet, useEscape, useReturnFocus } from "./surfaces";

/** A menu anchored under a trigger, rendered into `document.body` so that no
 * ancestor's `overflow: hidden` can clip it (#53: the dashboard cards clip).
 * Fixed to the trigger's rectangle, kept inside the viewport, and closed by a
 * click outside or Escape, which returns focus to the trigger. */
export function Popover({ id, open, anchor, onClose, label, children }: {
  /** The id the trigger's `aria-controls` names, so its `aria-expanded` is
   * tied to this menu rather than announced about nothing in particular. */
  id?: string;
  open: boolean;
  anchor: RefObject<HTMLElement | null>;
  onClose(): void;
  label: string;
  children: ReactNode;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number }>();

  // Place under the trigger, then pull back inside the viewport once the menu
  // has a size. Both run before paint, so the menu never appears misplaced.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = anchor.current?.getBoundingClientRect();
      if (a) setAt({ top: a.bottom + 4, left: a.left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, anchor]);

  useLayoutEffect(() => {
    const el = menu.current;
    if (!el || !at) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // no layout (jsdom): leave it where it is
    const a = anchor.current?.getBoundingClientRect();
    const left = Math.max(8, Math.min(at.left, window.innerWidth - r.width - 8));
    const above = a ? a.top - r.height - 4 : at.top;
    const top = at.top + r.height > window.innerHeight - 8 && above > 8 ? above : at.top;
    if (left !== at.left || top !== at.top) setAt({ top, left });
  }, [at, anchor]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
      anchor.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, anchor]);

  if (!open) return null;
  return createPortal(
    <>
      {/* A click-away layer, not a control: Escape closes the menu for the keyboard. */}
      <div className="backdrop popover-backdrop" role="presentation" onClick={onClose} />
      <div ref={menu} id={id} className="rail-menu popover" role="group" aria-label={label} style={{ top: at?.top ?? 0, left: at?.left ?? 0 }}>
        {children}
      </div>
    </>,
    document.body,
  );
}

/** Server prose with a quoted span in it: the API names a command or a field
 * in backticks (`zenith-api cell register`), and the quote should read as code
 * here rather than as two stray characters around it. A text with an unpaired
 * backtick is shown as written. */
export function Ticks({ text }: { text: string }) {
  const parts = text.split("`");
  if (parts.length < 3 || parts.length % 2 === 0) return <>{text}</>;
  return <>{parts.map((p, i) => (i % 2 === 1 ? <code key={i}>{p}</code> : p))}</>;
}

/** Whether two sentences say the same thing to a reader: case, surrounding
 * space and a closing full stop or colon do not make a second sentence (UX-009,
 * 2026-11-01 review: *"Your role does not allow this."* above *"your role does
 * not allow this"*). */
export function sameSentence(a: string, b: string): boolean {
  const norm = (s: string) => s.trim().replace(/[.:;!\s]+$/u, "").replace(/\s+/gu, " ").toLowerCase();
  return norm(a) === norm(b);
}

export function ProblemBox({ problem, onDismiss, head: given, fold = false, fieldLabels }: {
  problem: Problem; onDismiss?: () => void;
  /** The form's own label for an API field name, so a field error names the
   * field as the operator reads it (UX-009). A field the map does not name is
   * shown as the API sent it. */
  fieldLabels?: Record<string, string>;
  /** A headline the caller knows to be true of this one refusal, where the
   * reason code's canned sentence or the server's message is not the best
   * first line (#1788: a candidate this service's own gate refused, now
   * `conflict` under ADR-0393). */
  head?: string;
  /** The server's own words and the request id go under a closed *Details*,
   * never as body text (ADR-0413 decision 2, `2a`): the firmware act's
   * refusals only, on the device page once its panel has closed, and the
   * panel's own failed read. Inside the open panel a refusal is one line
   * and no box (#1839). Every other caller keeps the message and the field
   * list open, with the request id alone under *Details* (UX-009). */
  fold?: boolean;
}) {
  // The headline may be the server's own message, when the reason code alone
  // cannot say why (#100). Then it is not repeated underneath.
  const word = useTermIfScoped();
  const head = given ?? headline(problem, word);
  const message = problem.message && !sameSentence(problem.message, head) ? problem.message : undefined;
  const fields = problem.details && problem.details.length > 0 && (
    <ul className="problem-fields">
      {problem.details.map((d) => {
        const label = fieldLabels?.[d.field];
        return <li key={d.field}>{label ? <strong>{label}</strong> : <code>{d.field}</code>} {d.message}</li>;
      })}
    </ul>
  );
  // The request id is for support and means nothing to an operator, so it is
  // under a closed *Details* for every caller (UX-009), never body text.
  const requestId = problem.request_id && (
    <details className="problem-more">
      <summary>Details</summary>
      <div className="problem-id">Request <code>{problem.request_id}</code></div>
    </details>
  );
  return (
    <div role="alert" className="problem">
      <div>
        <strong><Ticks text={head} /></strong>
        {fold ? (message || fields || problem.request_id) && (
          <details className="problem-more">
            <summary>Details</summary>
            {message && <div className="problem-detail">{message}</div>}
            {fields}
            {problem.request_id && <div className="problem-id">Request <code>{problem.request_id}</code></div>}
          </details>
        ) : <>
          {message && <div className="problem-detail"><Ticks text={message} /></div>}
          {fields}
          {requestId}
        </>}
      </div>
      {onDismiss && <button type="button" className="btn btn-quiet" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

/** Whether a page is drawn as a tab of a workspace page (#874,
 * `shell/TabbedPage.tsx`): the workspace page owns the one `h1`, and the
 * page's own title is a section's under it. A page drawn alone has none set. */
export const InTab = createContext(false);

/** A page's title: its `h1` when it is the page, an `h2` when it is a tab of
 * one. Every page that is also a tab titles itself through this, so a
 * screen reader's outline has one page and its sections, never two pages. */
export function PageTitle({ children }: { children: ReactNode }) {
  return useContext(InTab) ? <h2>{children}</h2> : <h1>{children}</h1>;
}

/** An empty state: one line and one action (#1890, ruled 2026-10-20). Why it
 * is empty goes behind the ⓘ beside the line (`info`), and what to do about it
 * is `action`. `children` is the older second paragraph, kept for the screens
 * #1890 has not reached; a new empty state does not use it. */
export function Empty({ title, info, action, children }: { title: string; info?: ReactNode; action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title"><span>{title}</span>{info && <Info label="Why it is empty">{info}</Info>}</p>
      {action && <div className="empty-action">{action}</div>}
      {children && <p>{children}</p>}
    </div>
  );
}

/** The rest of an explanation, behind one disclosure (#1678, ruled 2026-10-11:
 * one shared component, built on `<details>`). A page leads with what it is for
 * or what to do next, and what a copy edit moves out of that lead goes here
 * whole: nothing is cut. **What an operator has to read before acting is never
 * put behind it** — a warning, a refusal, what an act will send or remove —
 * because a closed disclosure is the paragraph nobody opened. `web/COPY.md`,
 * *"Learn more"*, says when to use it.
 *
 * Native `<details>`, as every other disclosure in the application is: the
 * browser supplies the keyboard, the expanded state a screen reader announces
 * and find-in-page, so there is no state here and no script. **The content is
 * in the document while it is closed.** A `Restates` sentence moved into one
 * is still text a test can assert, and `src/test/setup.ts`'s guard still reads
 * it. `Compartments`' run list renders its table only when open because that
 * table is large; prose is not, and is not treated that way.
 *
 * **It reads as a link** (ruled 2026-10-11): the summary wears the link's
 * color and underline, and no triangle. That is `theme.css`'s alone. The
 * element is still a `<summary>`, never an `<a>` or a button, so nothing
 * above changes and there is no address to follow.
 *
 * `summary` is "Learn more" unless the page gives it words: where one page
 * carries several, each says what it opens. */
export function LearnMore({ summary = "Learn more", children }: { summary?: string; children: ReactNode }) {
  return (
    <details className="learn-more">
      <summary>{summary}</summary>
      <div className="learn-more-body">{children}</div>
    </details>
  );
}

/** An explanation behind an ⓘ beside the label it explains (#1890, ruled by
 * Gabriel Provencher on 2026-10-20: *"the ⓘ is a hover/focus popover"*). It
 * opens on hover and on keyboard focus, beside its button, and takes no room
 * on the page. `web/COPY.md`, *"Learn more"*, says when this is used and when
 * `LearnMore` is.
 *
 * **The keyboard, the reader and touch, once here.** The button is a real
 * `<button>` named by `label`; it opens on focus and on hover, a press pins it
 * open (which is what a tap does on touch) and a second press closes it,
 * Escape closes it and leaves focus on the button, and a press outside closes
 * a pinned one. Its explanation is a `role="tooltip"` the button names with
 * `aria-describedby`, so a screen reader reads it with the button, and
 * `aria-expanded` says whether it is drawn.
 *
 * **The explanation is in the document while it is closed**, as `LearnMore`'s
 * is: `hidden` keeps it off the screen and out of the reading order, and the
 * description still resolves to it. A `Restates` sentence moved into one is
 * still text a test can assert, and `src/test/setup.ts`'s guard still reads it.
 *
 * It is drawn `position: fixed` from the button's rectangle, kept inside the
 * viewport, so a card's `overflow: hidden` cannot clip it, and it stays a child
 * of the button's wrapper so that moving the pointer onto it keeps it open.
 * Prose only: a link or a control belongs on the page, not in a tooltip. */
export function Info({ label, children, className }: {
  /** The button's accessible name: what the explanation is about, as
   * "About this page" or "About applying". */
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  // A press or Escape closes it until the pointer and the focus have left, so
  // the hover or the focus that opened it does not hold it open.
  const [shut, setShut] = useState(false);
  const open = !shut && (hover || focus || pinned);
  const [at, setAt] = useState<CSSProperties>();

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = button.current?.getBoundingClientRect();
      const t = tip.current?.getBoundingClientRect();
      if (!b) return;
      const w = t && t.width > 0 ? t.width : 0;
      const h = t && t.height > 0 ? t.height : 0;
      const left = Math.max(8, Math.min(b.left, window.innerWidth - w - 8));
      const below = b.bottom + 6;
      const top = h && below + h > window.innerHeight - 8 && b.top - h - 6 > 8 ? b.top - h - 6 : below;
      setAt({ top, left });
    };
    place();
    // Once more after the first paint has given it a size.
    const frame = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      setPinned(false);
      setShut(true);
    };
    const onDown = (e: PointerEvent) => {
      if (wrap.current && e.target instanceof Node && wrap.current.contains(e.target)) return;
      setPinned(false);
      setHover(false);
    };
    // Captured, so the Escape is this tooltip's before a drawer's or a sheet's.
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown);
    return () => { document.removeEventListener("keydown", onKey, true); document.removeEventListener("pointerdown", onDown); };
  }, [open]);

  return (
    <span ref={wrap} className={className ? `info ${className}` : "info"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); if (!focus) setShut(false); }}>
      <button ref={button} type="button" className="info-btn" aria-label={label} aria-describedby={id} aria-expanded={open}
        onFocus={() => setFocus(true)}
        onBlur={() => { setFocus(false); setPinned(false); setShut(false); }}
        // A press pins it open, and a press on a pinned one closes it.
        onClick={() => { if (pinned) { setPinned(false); setShut(true); } else { setShut(false); setPinned(true); } }}>
        <Glyph icon={InfoIcon} />
      </button>
      <span ref={tip} id={id} role="tooltip" className="info-tip" hidden={!open} style={at}>{children}</span>
    </span>
  );
}

export function Loading({ label = "Loading", srOnly = false }: { label?: string;
  /** Rendered for assistive technology alone (`.sr-only`), where a skeleton is
   * what the eye gets (#1319): the sentence is still the polite status. */
  srOnly?: boolean }) {
  return <p className={srOnly ? "sr-only" : "muted"} aria-live="polite">{label}…</p>;
}

/** How many placeholder rows a table draws before its rows arrive (#1319). A
 * visual constant and not a §29 value, in the distinction `importcsv.MaxBytes`
 * records: eight rows at these tables' row height is about the half-screen
 * under a page head on a laptop, so a longer answer grows the table only below
 * the fold and a shorter one shortens a page that was already short. GitLab's
 * skeleton is a fixed shape too. Moving it is this line; remembering a list's
 * last-seen count per viewer is its own item. */
export const SKELETON_ROWS = 8;

/** How many cells a header row has: the elements in `head`, through any
 * fragment it is built from, so the placeholder rows under it have one cell
 * per column without a page repeating its count. A `false` from a
 * conditional column is not an element and is not counted. */
export function headCells(head: ReactNode): number {
  return Children.toArray(head).reduce<number>((n, c) =>
    n + (isValidElement<{ children?: ReactNode }>(c) && c.type === Fragment ? headCells(c.props.children) : 1), 0);
}

/** The placeholder rows of a table that is still being read (#1319): `rows`
 * of `columns` cells, each a `.skeleton` bar — the dashboard's class, with its
 * reduced-motion guard — and no text. Rendered inside a `<tbody aria-hidden>`,
 * so a screen reader meets a named, busy table that is empty rather than eight
 * rows of nothing. */
export function SkeletonRows({ columns, rows = SKELETON_ROWS }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>{Array.from({ length: columns }, (_, j) => <td key={j}><span className="skeleton skeleton-cell" /></td>)}</tr>
      ))}
    </>
  );
}

/** A table laid out before its rows arrive (#1319), for `ListPage`'s
 * `skeleton`: the same table by name and classes, its real header — the page
 * hands over the `<th>` set its ready table renders, so the two cannot differ
 * — over `SKELETON_ROWS` placeholder rows, `aria-busy` while it stands. The
 * rows replace it in place when the read answers, so nothing around the table
 * moves. A table that draws its own frame (the device lists) builds the same
 * thing from `SkeletonRows`. */
export function TableSkeleton({ label, className, head }: { label: string; className?: string; head: ReactNode }) {
  return (
    <div className="table-wrap">
      <table className={className} aria-label={label} aria-busy="true">
        <thead><tr>{head}</tr></thead>
        <tbody aria-hidden="true"><SkeletonRows columns={headCells(head)} /></tbody>
      </table>
    </div>
  );
}

export interface FieldSpec {
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "password" | "number" | "select" | "checkbox" | "rows";
  required?: boolean;
  pattern?: string;
  /** What the field means, behind the ⓘ beside its label (#1900): prose only,
   * never a link, as every ⓘ is (`web/COPY.md`, *"Learn more" and the ⓘ*). */
  hint?: string;
  /** What an operator must read before submitting: a warning or a refusal,
   * drawn in the open under the field and never behind the ⓘ (#1900). A
   * field's explanation is `hint`; this is only for the sentence that stops a
   * wrong act. */
  warning?: string;
  /** Drawn and not editable: a checkbox whose every value but the one shown
   * the server refuses. Say why in `warning`, in the open. */
  disabled?: boolean;
  /** A short line drawn under the field from the form's values, for where a
   * value came from — *From Google Maps* on a field New site filled (ADR-0520
   * decision 2a). Absent, or answering nothing, draws nothing. */
  note?: (values: FormValues) => string | undefined;
  options?: { value: string; label: string }[];
  /** For `rows`: the columns each row has. A `rows` field's value is a list of
   * records keyed by column name, one per row, and every value in a record is
   * the input's string — the caller types it, the way it types every other
   * field. `required` on the field means at least one row; `required` on a
   * column is the browser's, per row. Used where a network takes a set rather
   * than a value — the per-key entries of an MPSK network (#225). */
  columns?: RowColumn[];
  /** For `rows`: the words on the button that adds one. "Add row" by default. */
  addLabel?: string;
  /** What an empty field shows, in grey, and never a value (#2097). On a
   * select it is the text of the empty first option: on a required select it
   * stands for "not chosen yet", and "Choose one" is the default; on an
   * optional one it names what choosing nothing means — the site's LED
   * setting's "Device default (on)" — and "—" is the default. The option is
   * there whether or not this is given; this only names it, and the select is
   * drawn in placeholder grey while that option is the one chosen. On a text
   * or number input it is the input's `placeholder` attribute, which is not
   * part of the input's value: the form's value stays empty until something
   * is typed, so nothing a placeholder says is ever submitted. */
  placeholder?: string;
  /** A field whose control is not an input. The form still owns the value, the
   * label and the error; only the widget is the caller's. Used where the choice
   * is a tree rather than a list — see `ScopeChooser`. The browser cannot
   * validate one, so a required field with a custom control holds the submit
   * button until it has a value. `form` is the whole form, for a control that
   * offers a value to another field — a place's address, time zone or country
   * (#2132) — which it writes with `patch` in the same edit as its own, and
   * only on a person's act. */
  control?: (value: string, set: (next: string) => void, id: string, form: { values: FormValues; patch(next: FormValues): void }) => ReactNode;
  /** A text field's length bounds, the document's (`BOUNDS`), so the browser refuses what the request would. */
  minLength?: number;
  maxLength?: number;
}

export interface RowColumn {
  name: string;
  label: string;
  type?: "text" | "number";
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}
/** One row of a `rows` field: the column's input text, by column name. */
export type Row = Record<string, string | undefined>;

export type FormValues = Record<string, string | number | boolean | Row[] | undefined>;

/** An optional field's value, or nothing to send. A request field is built
 * from a value that exists or it is omitted. */
export const str = (v: FormValues[string]) => (v === undefined || v === "" || Array.isArray(v) ? undefined : String(v));

/** A required field's value. Empty when the form somehow has none, and never
 * the literal "undefined": `String(undefined)` is nine characters that pass
 * for data, which is how a site was created with a customer id nobody chose
 * (#171). An identifier is not built from this — a form that cannot name its
 * parent does not send the request at all. */
export const text = (v: FormValues[string]) => str(v) ?? "";

/** A numeric field's value, or nothing to send. */
export const num = (v: FormValues[string]) => (v === undefined || v === "" || Array.isArray(v) ? undefined : Number(v));

/** A clearable field of an edit form: what the PATCH sends for it. The
 * document's PATCH is a JSON Merge Patch — a field left out keeps its value
 * and `null` clears one the schema marks nullable — so a blank input is two
 * different things: nothing, when the resource had no value, and `null`,
 * when it had one and the operator emptied it. The form tells them apart by
 * what it was opened with, which is `before`; `value` is the input mapped by
 * `num` or `str`. Only for fields the document marks nullable — on any other
 * `null` is a 400 (#197). */
export const clearable = <T,>(value: T | undefined, before: unknown): T | null | undefined =>
  value !== undefined ? value : (before === undefined || before === null || before === "" ? undefined : null);

/** A `rows` field's rows, or none. Anything that is not a list is no rows:
 * a scalar in a list's slot is a caller's mistake, not a row. */
export const rows = (v: FormValues[string]): Row[] => (Array.isArray(v) ? v : []);

/** A form from a field spec. Submits typed values; the caller maps them to the
 * request body. Shows the Problem the caller hands back. */
export function SpecForm({ fields, initial, submitLabel, onSubmit, onChange, onCancel, problem, busy, incomplete, subject }: {
  fields: FieldSpec[];
  initial?: FormValues;
  submitLabel: string;
  onSubmit(values: FormValues): void;
  /** Called on every edit, for a form whose field list depends on a choice
   * inside it — a security mode that decides which material is asked for.
   * Without it such a form can only react on submit, which asks someone to
   * submit a form to find out what it wants. */
  onChange?(values: FormValues): void;
  onCancel?: () => void;
  problem?: Problem;
  busy?: boolean;
  /** Holds the submit button disabled for an answer the caller keeps outside
   * the form's values: a choice prefilled from a read, which the form's own
   * state cannot hold without an effect writing it there (the claim form's
   * kind and type, #2122). */
  incomplete?: boolean;
  /** What the form edits, for the question asked before an unsent edit is
   * left behind — *Discard the changes to Guest?* (UX-011). */
  subject?: string;
}) {
  const [values, setValues] = useState<FormValues>(() => ({ ...(initial ?? {}) }));
  const [opened] = useState<FormValues>(() => ({ ...(initial ?? {}) }));
  // The form's own checks (UX-010, `field.tsx`): on submit every field, then,
  // as a person types, only the fields already in error.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);
  // An edit nobody has sent is guarded (UX-011); a submit stands the guard
  // down until the next edit, and a refusal puts it back.
  const [sent, setSent] = useState(false);
  const guard = useUnsavedGuard(!sent && !sameValues(values, opened), subject);
  const [answered, setAnswered] = useState(problem);
  if (problem !== answered) { setAnswered(problem); if (problem) setSent(false); }
  const edit = (next: FormValues) => {
    setValues(next); onChange?.(next); setSent(false);
    if (Object.keys(errors).length > 0) {
      const still: Record<string, string> = {};
      for (const f of fields) {
        const e = errors[f.name] !== undefined ? fieldError(f, next[f.name]) : undefined;
        if (e) still[f.name] = e;
      }
      setErrors(still);
    }
  };
  const base = useId();
  const form = useRef<HTMLFormElement>(null);
  // A form is opened by a button, and focus stayed on that button: a keyboard
  // or screen-reader user had to go looking for what had just appeared (#204).
  // Once, on mount, to the first field — the actions are not fields, and a
  // remount (a form keyed on what it edits) is a new opening. A label's ⓘ is
  // not the field: focused, it opened its explanation over the field (#2122).
  useEffect(() => {
    const first = form.current?.querySelector<HTMLElement>(
      ".field input:not([type=hidden]):not(:disabled), .field select:not(:disabled), .field textarea:not(:disabled), .field button:not(:disabled):not(.info-btn)",
    );
    first?.focus();
  }, []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found = formErrors(fields, values);
    setErrors(found);
    setTried(true);
    const first = fields.find((f) => found[f.name] !== undefined);
    if (first) {
      // To the first field in error, as the browser's own check did.
      const el = document.getElementById(`${base}-${first.name}`);
      const target = el?.matches("input, select, textarea, button") ? el
        : el?.closest(".field")?.querySelector<HTMLElement>("input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled):not(.info-btn)");
      target?.focus();
      return;
    }
    guard.release();
    setSent(true);
    onSubmit(values);
  };
  // Only for fields the browser will not check for us: a custom control, and
  // a required list with no row in it — the browser checks each row's inputs,
  // and a list with none has no input to check.
  const unanswered = fields.some((f) => (f.control && f.required && !values[f.name]) || (f.type === "rows" && f.required && rows(values[f.name]).length === 0));
  return (
    <form ref={form} className="form" onSubmit={submit} noValidate>
      {fields.map((f) => {
        const id = `${base}-${f.name}`;
        const server = problem?.details?.find((d) => d.field === f.name);
        // The form's own word first; the server's, for what only it can judge.
        const detail = errors[f.name] !== undefined ? { message: errors[f.name]! } : server;
        const v = values[f.name];
        // The error, the warning or the note under the field describe it (UX-010).
        const said = detail ? `${id}-error` : f.warning ? `${id}-warning` : undefined;
        const aria = { "aria-invalid": detail ? true : undefined, "aria-describedby": said } as const;
        if (f.type === "rows") {
          return (
            <RowsField key={f.name} field={f} id={id} rows={rows(v)} detail={detail}
              onChange={(next) => edit({ ...values, [f.name]: next })} />
          );
        }
        return (
          <div className={"field" + (detail ? " field-invalid" : "")} key={f.name}>
            <FieldLabel htmlFor={id} field={f} />
            {f.control ? f.control(String(v ?? ""), (next) => edit({ ...values, [f.name]: next }), id, { values, patch: (next) => edit({ ...values, ...next }) })
            : f.type === "select" ? (
              <select id={id} required={f.required} value={String(v ?? "")} {...aria}
                className={String(v ?? "") === "" ? "is-empty" : undefined}
                onChange={(e) => edit({ ...values, [f.name]: e.target.value })}>
                {/* Always an empty first option, required or not. Without one
                    the browser preselects the first real option — a choice the
                    person did not make, whose DOM value is real enough that
                    `required` passes while the form's own state is still empty,
                    because no change event ever fired. The two then disagree and
                    the request carries whatever the call site made of an absent
                    value (#171). */}
                <option value="">{f.placeholder ?? (f.required ? "Choose one" : "—")}</option>
                {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === "checkbox" ? (
              <input id={id} type="checkbox" checked={Boolean(v)} disabled={f.disabled} aria-describedby={said}
                onChange={(e) => edit({ ...values, [f.name]: e.target.checked })} />
            ) : (
              <input id={id} type={f.type ?? "text"} required={f.required} pattern={f.pattern} minLength={f.minLength} maxLength={f.maxLength} value={String(v ?? "")} {...aria}
                placeholder={f.placeholder}
                onChange={(e) => edit({ ...values, [f.name]: f.type === "number" ? (e.target.value === "" ? undefined : Number(e.target.value)) : e.target.value })} />
            )}
            {detail ? <div className="field-error" id={`${id}-error`}>{detail.message}</div> : f.warning && <div className="field-warning" id={`${id}-warning`}>{f.warning}</div>}
            {f.note?.(values) && <div className="field-note">{f.note(values)}</div>}
          </div>
        );
      })}
      {problem && !problem.details?.length && <ProblemBox problem={problem} />}
      {tried && <ErrorCount count={Object.keys(errors).length} />}
      <div className="actions">
        <button type="submit" className="btn btn-primary" disabled={busy || unanswered || incomplete}>{submitLabel}</button>
        {onCancel && <button type="button" className="btn" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

/** A `rows` field: a list of records, each a line of inputs, with a button
 * that adds a line and one on each line that removes it. The inputs are the
 * form's, so the browser validates each row's columns as it does any other
 * field; only "at least one row" is the form's to hold (see `unanswered`). Each
 * input is labelled by its column and its row number, so a row is addressable
 * by assistive technology and by a test without a heading per row. */
function RowsField({ field: f, id, rows: list, detail, onChange }: {
  field: FieldSpec; id: string; rows: Row[]; detail?: { message: string }; onChange(next: Row[]): void;
}) {
  const columns = f.columns ?? [];
  const setCell = (i: number, name: string, value: string) => onChange(list.map((r, j) => (j === i ? { ...r, [name]: value } : r)));
  return (
    <div className={"field field-rows" + (detail ? " field-invalid" : "")} role="group" aria-labelledby={id} aria-describedby={detail ? `${id}-error` : undefined}>
      <span className="field-label"><span className="label" id={id}>{f.label}</span>{optional(f) && <Optional />}{f.hint && <Info label={`About ${f.label}`}>{f.hint}</Info>}</span>
      {list.length > 0 && (
        <div className="rows" style={{ "--rows-cols": columns.length } as CSSProperties}>
          <div className="rows-head" aria-hidden="true">{columns.map((c) => <span key={c.name}>{c.label}</span>)}<span /></div>
          {list.map((r, i) => (
            <div className="rows-row" key={i}>
              {columns.map((c) => (
                <input key={c.name} type={c.type ?? "text"} aria-label={`${c.label} ${i + 1}`} required={c.required}
                  minLength={c.minLength} maxLength={c.maxLength} min={c.min} max={c.max} value={r[c.name] ?? ""}
                  onChange={(e) => setCell(i, c.name, e.target.value)} />
              ))}
              <button type="button" className="btn btn-quiet" aria-label={`Remove ${i + 1}`} onClick={() => onChange(list.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <div><button type="button" className="btn" onClick={() => onChange([...list, {}])}>{f.addLabel ?? "Add row"}</button></div>
      {detail ? <div className="field-error" id={`${id}-error`}>{detail.message}</div> : f.warning && <div className="field-warning">{f.warning}</div>}
    </div>
  );
}

/** A field's label, and the ⓘ beside it that holds the field's `hint` (#1900).
 * The ⓘ sits outside the `<label>` so the field's accessible name stays its
 * label alone. */
function FieldLabel({ htmlFor, field: f }: { htmlFor: string; field: FieldSpec }) {
  if (!f.hint && !optional(f)) return <label htmlFor={htmlFor}>{f.label}</label>;
  return <span className="field-label"><label htmlFor={htmlFor}>{f.label}</label>{optional(f) && <Optional />}{f.hint && <Info label={`About ${f.label}`}>{f.hint}</Info>}</span>;
}

/** Beside an optional field's label, outside the `<label>` so the field's
 * accessible name stays its label (UX-010); a required field says so through
 * its control's `required`. */
function Optional() {
  return <span className="field-optional">(optional)</span>;
}

/** Two-step delete: the row's button becomes the confirmation in place. */
export function ConfirmDelete({ what, onConfirm, busy, trigger = "Delete", tone, verb = "Delete", note }: { what: string; onConfirm(): void; busy?: boolean;
  /** What the unarmed button shows; "Delete" alone by default. An icon
   * beside the word keeps the word (#368): the name a screen reader gets is
   * the visible text either way. */
  trigger?: ReactNode;
  /** Draws the **unarmed** trigger in the danger colour rather than quiet.
   * Opt-in, and the default is unchanged, because in a table's row-actions a
   * red button per row is a wall of red that says nothing — the colour is
   * worth having where the control stands alone among controls that write
   * (#823). The armed confirmation is red either way. */
  tone?: "danger";
  /** The act the armed confirmation names — "Delete" by default. A removal
   * that is not a deletion in the operator's words says its own: a unit's
   * passphrase is revoked (#881). */
  verb?: string;
  /** One sentence the armed confirmation shows beneath its question: what
   * the act leaves behind, where the operator must read it before the click
   * (#2165, the unclaim). Absent, the armed form is the question and two
   * buttons, as every other caller has it. */
  note?: string;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) return <button type="button" className={"btn btn-icon " + (tone === "danger" ? "btn-danger" : "btn-quiet")} onClick={() => setArmed(true)}>{trigger}</button>;
  return (
    <span className="confirm">
      <span>{verb} {what}?</span>
      {note && <span className="confirm-note">{note}</span>}
      <button type="button" className="btn btn-danger" disabled={busy} onClick={onConfirm}>{verb} {what}</button>
      <button type="button" className="btn btn-quiet" onClick={() => setArmed(false)}>Keep</button>
    </span>
  );
}

/** The acts' icons (#368), from the one set (#2043): each name is a member of
 * `icons.tsx`, drawn by `Glyph`. Every icon is decorative — `aria-hidden`, with
 * the act's word beside it or on the button's accessible name — so nothing an
 * operator can do is told by a picture alone. */
export type IconName = "reboot" | "blink" | "upgrade" | "factory" | "console" | "edit" | "delete" | "more" | "refresh" | "ports" | "plan" | "power";

const ICONS: Record<IconName, LucideIcon> = {
  reboot: RebootIcon, blink: BlinkIcon, upgrade: FirmwareIcon, factory: FactoryIcon, console: ConsoleIcon, edit: EditIcon,
  delete: DeleteIcon, more: MoreIcon, refresh: RefreshIcon, ports: PortsIcon,
  // A plan: a sheet with a row added to it (#639).
  plan: PlanIcon,
  // A power symbol: the PoE cycle (ADR-0251).
  power: PowerIcon,
};

export function Icon({ name }: { name: IconName }) {
  return <Glyph icon={ICONS[name]} className={"icon icon-" + name} />;
}

/** Status pill: a dot and a word. State never travels by colour alone (ZENITH.md §18).
 * `info` is a state that is neither good nor bad — a planned device placed at a
 * location (#519) — drawn in the accent so it reads apart from the pool's grey
 * without borrowing a health colour. */
export function Pill({ tone = "none", title, children }: { tone?: "good" | "warn" | "serious" | "critical" | "info" | "none"; title?: string; children: ReactNode }) {
  return <span className={"pill pill-" + tone} title={title}><span className="dot" aria-hidden="true" />{children}</span>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An identifier, for where a person may need to copy one (UX review
 * 2026-11-01, UX-003, UX-004). Only a UUID is shortened, and to its *end*:
 * its start is where uuids printed together collide least by accident, but a
 * serial or a MAC is never cut, because its first eight characters are the
 * vendor's prefix and every device of a fleet would read the same. `full`
 * shows a UUID whole, for a detail row someone copies from. A name, where one
 * exists, is shown instead of this, never beside it. */
export function Id({ value, full, copy }: { value: string; full?: boolean; copy?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  const shown = !UUID.test(value)
    ? <code className="id">{value}</code>
    : <code className="id" title={value}>{full ? value : `…${value.slice(-8)}`}</code>;
  if (!copy) return shown;
  // `copy` names the value ("Serial", "ID") and draws a button that copies it
  // whole, a shortened UUID included (#2344; UX-003, UX-004). In a table it shows
  // on the row's hover and focus; elsewhere always.
  return (
    <span className="id-copyable">
      {shown}
      {/* It says Copied where the pointer or the thumb is, as well as in the shell's line. */}
      <button type="button" className={"btn btn-quiet btn-icon id-copy" + (copied ? " is-copied" : "")} aria-label={`Copy ${copy.toLowerCase()} ${value}`} title={`Copy ${copy.toLowerCase()}`}
        onClick={() => void copyValue(value, copy).then(setCopied)}>
        <Glyph icon={copied ? DoneIcon : CopyIcon} size={12} />
      </button>
    </span>
  );
}

/** Twelve hex digits as the address a person recognises: aa:bb:cc:00:00:01. */
export function mac(station: string): string {
  return station.length === 12 ? station.match(/../g)!.join(":") : station;
}

/** The address as six pairs rather than as seventeen characters. The hex is
 * tightened a little so each pair coheres as a chunk; the colons keep the
 * font's own spacing, which is what makes them read as joints between chunks.
 * One `letter-spacing` over the whole string cannot do this — it spaces every
 * character, the separators included, loosening exactly the joints that do the
 * chunking. So the pairs and the colons are separate spans, and the separator
 * gives back the sliver the preceding pair's negative tracking took. */
export function Mac({ station }: { station: string }) {
  const parts = mac(station).split(":");
  return (
    <code className="id mac">
      {parts.map((pair, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="mac-sep">:</span>}
          <span className="mac-pair">{pair}</span>
        </Fragment>
      ))}
    </code>
  );
}

export function When({ value }: { value?: string }) {
  if (!value) return <span className="muted">—</span>;
  const d = new Date(value);
  return <time dateTime={value} title={d.toISOString()}>{d.toLocaleString()}</time>;
}

/** One tab of a `Tabs` strip: its id is what the address carries. */
export interface Tab { id: string; label: ReactNode; title?: string }

/** A tab strip (#388): one `tablist` of buttons, one selected, arrow keys and
 * Home/End moving between them the way a native strip does, and the panel
 * for the selected one rendered by the caller through `TabPanel` with the
 * same id, so the strip and its panel are wired to each other for a screen
 * reader without either knowing the other's element. Only the selected
 * tab is in the Tab order; the arrows are how the rest are reached. Which
 * tab is selected is the caller's — a facet in the address on the device
 * page — so the strip holds no state of its own. */
export function Tabs({ tabs, current, onSelect, label }: { tabs: Tab[]; current: string; onSelect(id: string): void; label: string }) {
  const onKey = (e: ReactKeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = tabs.length;
    const to = e.key === "ArrowRight" ? (i + 1) % n : e.key === "ArrowLeft" ? (i - 1 + n) % n : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : undefined;
    if (to === undefined) return;
    e.preventDefault();
    const next = tabs[to];
    if (!next) return;
    onSelect(next.id);
    (e.currentTarget.parentElement?.children[to] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={t.id === current} aria-controls={`panel-${t.id}`}
          tabIndex={t.id === current ? 0 : -1} title={t.title} onClick={() => onSelect(t.id)} onKeyDown={(e) => onKey(e, i)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** The panel of the selected tab, named by its tab. */
export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return <div className="tab-panel" role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`}>{children}</div>;
}
